import { logoIconSvg, formatDiopter, formatAxis, formatIop } from "@clinic/ui";
import type { ClinicConfig, EyeRecord } from "@clinic/types";

const teal = "#1D9E75";
const tealDark = "#0F6E56";
const tealLight = "#5DCAA5";
const ink = "#0a1410";
const muted = "#5F7A74";

export interface PrescriptionDocInput {
  clinic: Pick<ClinicConfig, "name" | "address" | "phone" | "email"> | null;
  patientName: string;
  idNumber?: string;
  dob?: string;
  phone?: string;
  record: Pick<EyeRecord, "rightEye" | "leftEye" | "diagnosis" | "prescription" | "notes" | "visitDate">;
  doctorName: string;
  prescriptionId: string;
  issuedAt: string;
}

function esc(s: string | undefined | null): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
function gbDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

/** Build the standalone A4 prescription HTML (mirrors the server PDF letterhead). */
export function prescriptionDocHtml(input: PrescriptionDocInput): string {
  const { clinic, record } = input;
  const row = (label: string, eye: EyeRecord["rightEye"]) => `
    <tr>
      <td class="eye">${label}</td>
      <td class="mono">${formatDiopter(eye.sph)}</td>
      <td class="mono">${formatDiopter(eye.cyl)}</td>
      <td class="mono">${formatAxis(eye.axis)}</td>
      <td class="mono">${formatDiopter(eye.add)}</td>
      <td class="mono">${formatIop(eye.iop)}</td>
    </tr>`;

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Prescription ${esc(input.prescriptionId)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:"Inter",system-ui,Arial,sans-serif; color:${ink}; font-size:13px; line-height:1.6; padding:40px; }
  .toolbar { position:fixed; top:12px; right:12px; }
  .toolbar button { background:${teal}; color:#fff; border:0; border-radius:8px; padding:8px 14px; font-size:13px; cursor:pointer; }
  .letterhead { display:flex; align-items:center; gap:20px; padding-bottom:18px; border-bottom:2px solid ${teal}; }
  .lh-name { font-family:Georgia,serif; font-size:28px; letter-spacing:2px; }
  .lh-sub { font-family:Georgia,serif; font-size:13px; letter-spacing:4px; color:${tealDark}; margin-top:2px; }
  .lh-tag { font-size:11px; letter-spacing:2px; color:${muted}; margin-top:6px; }
  .meta { display:flex; justify-content:space-between; margin-top:18px; font-size:12px; color:${muted}; }
  h1 { font-size:20px; font-weight:600; margin:28px 0 4px; }
  .rx-id { font-family:"JetBrains Mono",monospace; font-size:11px; color:${muted}; }
  .panel { border:1px solid #d7e8e1; border-radius:8px; padding:16px 20px; margin-top:16px; }
  .panel h2 { font-size:13px; font-weight:600; color:${tealDark}; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
  .grid { display:grid; grid-template-columns:1fr 1fr; gap:4px 24px; }
  table.rx { width:100%; border-collapse:collapse; margin-top:8px; }
  table.rx th { background:#F0FAF6; color:${tealDark}; font-size:11px; text-transform:uppercase; letter-spacing:1px; padding:8px 10px; text-align:left; border-bottom:1px solid #d7e8e1; }
  table.rx td { padding:10px; border-bottom:1px solid #edf5f1; }
  td.eye { font-weight:600; color:${tealDark}; }
  .mono { font-family:"JetBrains Mono",ui-monospace,monospace; font-size:13px; }
  .sig { margin-top:56px; display:flex; justify-content:space-between; align-items:flex-end; }
  .sig-line { border-top:1px solid ${ink}; width:220px; padding-top:6px; font-size:12px; }
  .footer { margin-top:48px; padding-top:12px; border-top:1px solid #d7e8e1; font-size:10px; color:${muted}; text-align:center; }
  @media print { .toolbar { display:none; } body { padding:0; } }
</style></head>
<body>
  <div class="toolbar"><button onclick="window.print()">Save as PDF / Print</button></div>
  <div class="letterhead">
    <div>${logoIconSvg(72)}</div>
    <div>
      <div class="lh-name">OTZEM CHASSID</div>
      <div class="lh-sub">EYE CLINIC</div>
      <div class="lh-tag">PRIVATE OPHTHALMOLOGY PRACTICE</div>
    </div>
  </div>
  <div class="meta">
    <span>${esc(clinic?.address)} · ${esc(clinic?.phone)} · ${esc(clinic?.email)}</span>
    <span>Issued: ${gbDate(input.issuedAt)}</span>
  </div>

  <h1>Optical Prescription</h1>
  <div class="rx-id">Rx ${esc(input.prescriptionId)}</div>

  <div class="panel">
    <h2>Patient</h2>
    <div class="grid">
      <div><strong>${esc(input.patientName)}</strong></div>
      <div>ID: <span class="mono">${esc(input.idNumber) || "—"}</span></div>
      <div>Date of birth: ${gbDate(input.dob)}</div>
      <div>Phone: ${esc(input.phone) || "—"}</div>
    </div>
  </div>

  <div class="panel">
    <h2>Refraction — ${gbDate(record.visitDate)}</h2>
    <table class="rx">
      <thead><tr><th></th><th>Sphere</th><th>Cylinder</th><th>Axis</th><th>Add</th><th>IOP</th></tr></thead>
      <tbody>
        ${row("OD (Right)", record.rightEye)}
        ${row("OS (Left)", record.leftEye)}
      </tbody>
    </table>
    <p style="margin-top:12px;"><strong>Diagnosis:</strong> ${esc(record.diagnosis)}</p>
    ${record.prescription ? `<p style="margin-top:8px;"><strong>Prescription:</strong> ${esc(record.prescription)}</p>` : ""}
    ${record.notes ? `<p style="margin-top:4px;color:${muted};">${esc(record.notes)}</p>` : ""}
  </div>

  <div class="sig">
    <div class="sig-line"><strong>${esc(input.doctorName)}</strong><br>Ophthalmologist</div>
    <div class="sig-line" style="text-align:center;color:${muted};">Signature &amp; stamp</div>
  </div>

  <div class="footer">
    Generated by ${esc(clinic?.name) || "Otzem Chassid Eye Clinic"} clinic system ·
    <span style="color:${tealLight};">Valid for 12 months from date of issue</span>
  </div>
  <script>window.onload=function(){setTimeout(function(){try{window.focus();window.print();}catch(e){}},350)}</script>
</body></html>`;
}

/** Open the prescription in a new window and trigger the browser's print → Save as PDF. */
export function openPrescriptionDoc(input: PrescriptionDocInput): void {
  const win = window.open("", "_blank", "width=840,height=1040");
  if (!win) {
    alert("Please allow pop-ups for this site to download the prescription PDF.");
    return;
  }
  win.document.open();
  win.document.write(prescriptionDocHtml(input));
  win.document.close();
}
